import { EXPLORE_IMAGES } from '../exploreImages';
import { OFFICIAL_SOURCES, LAST_VERIFIED } from '../officialSources';

export const LANGTANG_JOURNEY_BASE = {
	slug: 'langtang-valley',
	title: 'Langtang Valley Journey',
	eyebrow: 'Journey Blueprint',
	subtitle: 'Closest Himalayan trek to Kathmandu — Tamang heritage, alpine meadows, and Kyanjin Gompa.',
	lead: 'No mountain flight required. A week in the Langtang Valley delivers glaciers, monasteries, and 7,000m peaks within driving distance of Kathmandu.',
	hero: EXPLORE_IMAGES.trekking,
	lastVerified: LAST_VERIFIED,
	coverage: 72,
	status: 'complete',
	destination: { slug: 'langtang-valley', name: 'Langtang Valley' },
	district: { slug: 'rasuwa', name: 'Rasuwa' },

	quickFacts: [
		{ value: '7–9 days', label: 'Trek duration' },
		{ value: '4,984m', label: 'Kyanjin Ri summit' },
		{ value: '3,870m', label: 'Kyanjin Gompa' },
		{ value: 'Mar–May, Oct–Nov', label: 'Best seasons' },
		{ value: 'Moderate', label: 'Difficulty' },
		{ value: '$700–1,500', label: 'Budget trek (excl. flights)' },
	],

	primarySource: OFFICIAL_SOURCES.dnp,
	officialSources: [
		OFFICIAL_SOURCES.dnp,
		OFFICIAL_SOURCES.ntb,
		OFFICIAL_SOURCES.immigration,
		OFFICIAL_SOURCES.touristPolice,
	],

	faqs: [
		{ q: 'Why choose Langtang over EBC or ABC?', a: 'Closest major trek to Kathmandu — no Lukla flight. Tamang culture, quieter trails than Annapurna, and dramatic Langtang Lirung (7,227m) views. Rebuilt after 2015 earthquake — community welcomes trekkers.' },
		{ q: 'Is Langtang safe after the earthquake?', a: 'Trail and teahouses have been rebuilt. The route is well-established and regularly trekked. Check current conditions with NTB before monsoon season.' },
		{ q: 'What permits do I need?', a: 'Langtang National Park entry permit (DNPWC) plus TIMS depending on current policy. Obtain in Kathmandu at NTB or park permit counter.' },
		{ q: 'How do I get to the trailhead?', a: 'Drive from Kathmandu to Syabrubesi (7–8 hours by jeep/bus). Start walking from Syabrubesi — no flight needed.' },
	],

	related: [
		{ label: 'Trekking in Nepal hub', to: '/trekking' },
		{ label: 'Trekking permits', to: '/guides/tims-card-trekking-permits' },
		{ label: 'Travel insurance', to: '/guides/travel-insurance-for-nepal-trekking' },
		{ label: 'Everest Base Camp journey', to: '/journeys/everest-base-camp' },
		{ label: 'Annapurna Base Camp journey', to: '/journeys/annapurna-base-camp' },
		{ label: 'Safety & emergency', to: '/safety' },
	],

	steps: [
		{
			id: 'dream',
			name: 'Dream',
			icon: '💭',
			question: 'Is Langtang Valley right for me?',
			lead: 'Moderate trek, rich culture, no flight dependency — ideal second trek or alternative to crowded routes.',
			sections: [
				{ id: 'why', question: 'Why Langtang?', answer: 'Trek through Tamang villages, rhododendron forest, and yak pastures to Kyanjin Gompa beneath Langtang Lirung. Less crowded than EBC/ABC. Road access from Kathmandu.' },
				{ id: 'fitness', question: 'Fitness level', answer: 'Moderate — 5–6 hours walking per day. Max sleeping altitude 3,870m at Kyanjin Gompa; optional Kyanjin Ri day hike to 4,984m.' },
			],
			nextLabel: 'Research →',
		},
		{
			id: 'research',
			name: 'Research',
			icon: '🔍',
			question: 'Season, duration, cost',
			lead: 'Plan before you book.',
			sections: [
				{ id: 'season', question: 'Best time', answer: 'October–November and March–May. Winter possible with cold gear. Monsoon (June–August) — landslide risk on road to Syabrubesi.' },
				{ id: 'cost', question: 'Budget', answer: '$700–1,500 total excluding international flights. Cheaper than EBC — no Lukla flights.' },
			],
			nextLabel: 'Visa →',
		},
		{
			id: 'visa',
			name: 'Visa',
			icon: '🛂',
			question: 'Nepal tourist visa',
			lead: 'Standard 30-day tourist visa.',
			sections: [
				{ id: 'visa', question: 'Requirements', answer: 'Same as all Nepal treks — tourist visa on arrival or online. USD 50 for 30 days.', source: OFFICIAL_SOURCES.immigration },
			],
			nextLabel: 'Flights →',
		},
		{
			id: 'flights',
			name: 'Flights',
			icon: '✈️',
			question: 'International flights only',
			lead: 'Fly to Kathmandu — Langtang starts by road, not air.',
			sections: [
				{ id: 'intl', question: 'Getting to Nepal', answer: 'Fly to Kathmandu. No domestic flight required for Langtang — jeep or bus to Syabrubesi (7–8 hr).' },
			],
			nextLabel: 'Insurance →',
		},
		{
			id: 'insurance',
			name: 'Insurance',
			icon: '🛡',
			question: 'Insurance for Langtang',
			lead: 'Cover 5,000m if climbing Kyanjin Ri; 4,500m minimum otherwise.',
			sections: [
				{ id: 'cover', question: 'Coverage', answer: 'Helicopter evacuation, medical expenses, 4,500–5,000m altitude. Langtang was heavily served by helicopter rescue after 2015 — insurance is essential.' },
			],
			nextLabel: 'Packing →',
		},
		{
			id: 'packing',
			name: 'Packing',
			icon: '🎒',
			question: 'Gear for Langtang',
			lead: 'Standard teahouse trek kit — cold at Kyanjin Gompa.',
			sections: [
				{ id: 'gear', question: 'Essentials', answer: 'Boots, sleeping bag (-10°C), down jacket, rain gear, poles. Teahouses provide blankets but bring a warm bag for Kyanjin.' },
			],
			nextLabel: 'Permits →',
		},
		{
			id: 'permits',
			name: 'Permits',
			icon: '📋',
			question: 'Langtang National Park permit',
			lead: 'Park entry permit required — TIMS may also apply.',
			sections: [
				{ id: 'park', question: 'Park permit', answer: 'Langtang National Park entry: NPR 3,000 for foreign nationals. Obtain at NTB Kathmandu or park checkpoint.', source: OFFICIAL_SOURCES.dnp },
				{ id: 'tims', question: 'TIMS', answer: 'Verify current TIMS requirement with NTB before travel.', source: OFFICIAL_SOURCES.ntb },
			],
			nextLabel: 'Permit offices →',
		},
		{
			id: 'permit-offices',
			name: 'Permit Offices',
			icon: '📍',
			question: 'Where to get permits',
			lead: 'Kathmandu before you drive to Syabrubesi.',
			sections: [
				{ id: 'office', question: 'NTB Kathmandu', answer: 'Nepal Tourism Board, Bhrikutimandap — Langtang National Park permit. Passport, photos, NPR cash.' },
			],
			nextLabel: 'Guide →',
		},
		{
			id: 'guide',
			name: 'Guide',
			icon: '🧭',
			question: 'Guide for Langtang?',
			lead: 'Commonly trekked independently — trail is clear.',
			sections: [
				{ id: 'guide', question: 'Independent vs guided', answer: 'Well-marked teahouse trail. Independent trekking is common. Guide adds cultural context (Tamang heritage) and navigation in snow.', source: OFFICIAL_SOURCES.ntb },
			],
			nextLabel: 'Agencies →',
		},
		{
			id: 'sherpa',
			name: 'Sherpa',
			icon: '🏔',
			question: 'Hiring guides',
			lead: 'TAAN agencies in Kathmandu arrange Langtang guides.',
			sections: [
				{ id: 'hire', question: 'How to hire', answer: 'Kathmandu Thamel agencies run Langtang packages. Guide: $20–28/day.' },
			],
			nextLabel: 'Porter →',
		},
		{
			id: 'porter',
			name: 'Porter',
			icon: '📦',
			question: 'Porter logistics',
			lead: 'Optional — moderate trek manageable with light pack.',
			sections: [
				{ id: 'porter', question: 'Porter details', answer: 'USD 14–18/day. Useful if you prefer a light daypack on the steady climb to Kyanjin.' },
			],
			nextLabel: 'Kathmandu prep →',
		},
		{
			id: 'kathmandu',
			name: 'Kathmandu',
			icon: '🏙',
			question: 'Prep and drive to Syabrubesi',
			lead: 'Permits, gear, SIM — then jeep to trailhead.',
			sections: [
				{ id: 'prep', question: 'Before the trail', answer: 'Thamel: permits, gear, SIM, ATM. Book jeep to Syabrubesi (shared $15–25, private $120–180). Leave early — road is long.' },
			],
			nextLabel: 'Syabrubesi →',
		},
		{
			id: 'lukla',
			name: 'Syabrubesi',
			icon: '🚐',
			question: 'Road to trailhead',
			lead: '7–8 hour drive from Kathmandu — no flight.',
			sections: [
				{ id: 'road', question: 'Getting to Syabrubesi', answer: 'Jeep from Kathmandu (Machha Pokhari bus park or agency pickup). Road via Trishuli — landslide delays possible in monsoon. Sleep in Syabrubesi before trekking.' },
			],
			nextLabel: 'Trek →',
		},
		{
			id: 'trek',
			name: 'Trek',
			icon: '🥾',
			question: 'Day-by-day Langtang Valley',
			lead: '7 days Syabrubesi round trip to Kyanjin Gompa.',
			isTrekDays: true,
			sections: [
				{ id: 'route', question: 'Route', answer: 'Syabrubesi → Lama Hotel → Langtang village → Kyanjin Gompa → optional Kyanjin Ri → return. Rebuilt teahouses throughout.' },
			],
			nextLabel: 'Emergency →',
		},
		{
			id: 'emergency',
			name: 'Emergency',
			icon: '🚨',
			question: 'AMS and rescue',
			lead: 'Moderate altitude but AMS possible above 3,500m.',
			sections: [
				{ id: 'ams', question: 'Altitude', answer: 'Kyanjin Gompa at 3,870m — most trekkers acclimatize adequately. Kyanjin Ri (4,984m) is optional day hike — only if acclimatized. Helicopter evac available via Tourist Police 1144.' },
			],
			nextLabel: 'Return →',
		},
		{
			id: 'return',
			name: 'Return',
			icon: '🏠',
			question: 'Back to Kathmandu',
			lead: 'Jeep from Syabrubesi — allow full day for road.',
			sections: [
				{ id: 'home', question: 'Heading home', answer: 'Trek back to Syabrubesi, jeep to Kathmandu. Buffer 1 day before international flight.' },
			],
		},
	],

	trekDays: [
		{ day: 1, title: 'Drive Kathmandu → Syabrubesi', sleep: 'Syabrubesi (1,460m)', altitude: '1,460m', distance: '—', time: '7–8 hr drive', food: 'Teahouse', medical: 'Basic', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Long jeep ride — start trek next morning.' },
		{ day: 2, title: 'Syabrubesi → Lama Hotel', sleep: 'Lama Hotel (2,470m)', altitude: '2,470m', distance: '11 km', time: '6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Forest trail along Langtang Khola.' },
		{ day: 3, title: 'Lama Hotel → Langtang village', sleep: 'Langtang (3,430m)', altitude: '3,430m', distance: '10 km', time: '5–6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Tamang villages, mani walls, yak pastures.' },
		{ day: 4, title: 'Langtang → Kyanjin Gompa', sleep: 'Kyanjin Gompa (3,870m)', altitude: '3,870m', distance: '7 km', time: '4 hrs', food: 'Teahouse + bakery', medical: 'None', atm: 'No', signal: 'Intermittent', electricity: 'Limited', water: 'Purified', notes: 'Cheese factory, monastery. Langtang Lirung dominates.' },
		{ day: 5, title: 'Kyanjin Ri day hike (optional)', sleep: 'Kyanjin Gompa (3,870m)', altitude: '4,984m (Kyanjin Ri)', distance: '6 km', time: '5–6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Intermittent', electricity: 'Limited', water: 'Purified', notes: 'Acclimatization hike — panoramic 7,000m peaks.' },
		{ day: 6, title: 'Kyanjin → Lama Hotel (descent)', sleep: 'Lama Hotel (2,470m)', altitude: '2,470m', distance: '18 km', time: '6–7 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Long descent day.' },
		{ day: 7, title: 'Lama Hotel → Syabrubesi', sleep: 'Syabrubesi (1,460m)', altitude: '1,460m', distance: '11 km', time: '5 hrs', food: 'Teahouse', medical: 'Basic', atm: 'Limited', signal: 'Moderate', electricity: 'Yes', water: 'Yes', notes: 'Jeep to Kathmandu next day.' },
	],
};
